<script>
import ActionFooter from '@components/footer/action-footer.vue';
import MessageTemplateOptions from '@components/prospects/message-template-options.vue';
import {
  adminPanelComputed,
  adminPanelMethods,
  alertMethods,
  authComputed,
  campaignComputed,
  campaignMethods,
  modalMethods,
  prospectComputed,
  prospectMethods,
} from '@state/helpers';

export default {
  components: {
    ActionFooter,
    MessageTemplateOptions,
  },
  props: {},
  data() {
    return {
      isFormValid: true,
      isSending: false,
      hasUnsavedChanges: false,
    };
  },
  computed: {
    ...authComputed,
    ...adminPanelComputed,
    ...campaignComputed,
    ...prospectComputed,
    msgType() {
      return this.panelContent?.data?.msgType;
    },
    isEmailMsg() {
      return this.msgType && this.msgType === 'email';
    },
  },
  mounted() {
    this.setSelectedCampaign(this.campaigns[0]);
    this.setOverriddenCampaignTemplate(null);
  },
  methods: {
    ...adminPanelMethods,
    ...alertMethods,
    ...campaignMethods,
    ...modalMethods,
    ...prospectMethods,
    onSelectCampaign(msg) {
      this.setOverriddenCampaignTemplate(null);
      this.setSelectedCampaign(msg);
    },
    msgBodyValue() {
      if (this.isEmailMsg) {
        return this.overriddenCampaignTemplate
          ? this.overriddenCampaignTemplate.emailBody
          : this.selectedCampaign?.emailBody;
      } else {
        return this.overriddenCampaignTemplate
          ? this.overriddenCampaignTemplate.smsMessage
          : this.selectedCampaign?.smsMessage;
      }
    },
    buildEmailMsg() {
      const { emailSubject, emailBody } = this.overriddenCampaignTemplate
        ? this.overriddenCampaignTemplate
        : this.selectedCampaign;
      return { emailSubject, emailBody };
    },
    buildSmsMsg() {
      const { smsMessage } = this.overriddenCampaignTemplate
        ? this.overriddenCampaignTemplate
        : this.selectedCampaign;
      return { smsMessage };
    },
    updateProspectMsgSubject(value) {
      const { emailBody } = this.buildEmailMsg();
      this.setOverriddenCampaignTemplate({ emailSubject: value, emailBody });
    },
    updateProspectMsgBody(value, template) {
      let msg;
      if (this.isEmailMsg) {
        const { emailSubject } = this.buildEmailMsg();
        msg = { ...this.selectedCampaign, emailSubject, emailBody: value };
      } else {
        msg = { ...this.selectedCampaign, smsMessage: value };
      }
      this.setOverriddenCampaignTemplate(msg);
    },
    discardChanges() {
      this.overriddenCampaignTemplate
        ? this.$bvModal.show('unsaved-changes-modal')
        : this.setHideAdminPanel();
    },
    handleSendSuccess(msg) {
      this.getProspect(this.selectedProspect._id);
      this.setHideAdminPanel();
      this.setAlertText(msg);
      this.setShowAlert();
    },
    handleSendError(errors) {
      const msg = errors
        .map((error) => error.message)
        .join()
        .replace(/,/g, ', ');
      this.setNotifyModalContext({
        textMain: `Uh oh!`,
        textSecond: `${msg}`,
      });
      this.$bvModal.show('notify-modal');
    },
    buildMsgPayload() {
      const msg = { _id: this.selectedProspect._id, messageType: this.msgType };

      if (this.isEmailMsg) {
        msg.subject = this.buildEmailMsg().emailSubject;
        msg.message = this.buildEmailMsg().emailBody;
      } else {
        msg.message = this.buildSmsMsg().smsMessage;
      }

      return [msg];
    },
    send() {
      this.isSending = true;
      const prospects = this.buildMsgPayload();
      this.contactProspects({
        campaignId: this.selectedCampaign._id,
        prospects,
      })
        .then((res) => {
          if (res) {
            if (res.errors.length) {
              this.handleSendError(res.errors);
            } else {
              this.handleSendSuccess(res.message);
            }
          }
          this.isSending = false;
        })
        .catch((err) => {
          this.handleSendError([
            { message: 'Something went wrong. Please try again!' },
          ]);
          this.isSending = false;
          console.warn(err);
        });
    },
  },
  watch: {
    isAdminPanelActive(newVal, oldVal) {
      if (!newVal && oldVal) {
        this.setSelectedCampaign(this.campaigns[0]);
        this.setOverriddenCampaignTemplate(null);
      }
    },
  },
};
</script>

<template>
  <div class="prospect-contact-form">
    <MessageTemplateOptions
      :templates="campaigns"
      :isEmailMsg="isEmailMsg"
      @select-campaign-template="onSelectCampaign"
    />

    <div class="row" v-if="isEmailMsg">
      <div class="col">
        <label>Subject</label>
        <BaseInputText
          :value="
            overriddenCampaignTemplate
              ? overriddenCampaignTemplate.emailSubject
              : selectedCampaign && selectedCampaign.emailSubject
          "
          @input="updateProspectMsgSubject($event.target.value)"
        />
      </div>
    </div>
    <div class="row">
      <div class="col">
        <label>Message</label>
        <BaseInputTextarea
          type="textarea"
          rows="12"
          :value="msgBodyValue()"
          @input="updateProspectMsgBody($event.target.value)"
        />
      </div>
    </div>

    <ActionFooter>
      <BaseButton
        type="button"
        class="btn-primary btn-clear"
        :style="{ opacity: isSending ? 0.5 : 1 }"
        :disabled="!isFormValid || isSending"
        @click="discardChanges"
      >
        <svg width="14" height="18" viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-trash'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M10.5 1H14v2H0V1h3.5l1-1h5l1 1zM3 18c-1.1 0-2-.9-2-2V4h12v12c0 1.1-.9 2-2 2H3z" fill="#000"/></svg>
        Discard Changes
      </BaseButton>
      <span class="divider-vertical">&nbsp;</span>
      <BaseButton
        type="button"
        class="btn-primary btn-clear"
        :style="{ opacity: isSending ? 0.5 : 1 }"
        :disabled="!isFormValid || isSending"
        @click="send"
      >
        <BaseIcon v-if="isSending" name="sync" spin />
        <svg width="31" height="30" viewBox="0 0 31 30" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-mail'" v-if="!isSending" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M23.627 6.258L8.22 15.123c-.597.332-.531 1.195.067 1.428l3.552 1.494 9.53-8.4c.166-.166.431.066.265.265l-7.969 9.729v2.69a.786.786 0 001.395.53l2.125-2.59 4.117 1.76c.465.2 1.03-.1 1.096-.63l2.39-14.344c.133-.664-.597-1.13-1.162-.797z" fill="#fff"/></svg>
        Send <span>{{ isEmailMsg ? msgType : 'SMS' }}</span>
      </BaseButton>
    </ActionFooter>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.prospect-contact-form {
  .action-footer {
    .button.btn-clear,
    .button.btn-clear:active {
      padding: 0.5rem 2.4rem;
      text-transform: capitalize;
    }

    .icon-trash {
      color: white;
      height: 1.5rem;
    }

    .icon-mail {
      height: 2.3rem;
    }

    .fa-spin {
      margin-right: 1rem;
    }
  }
}
</style>
